/**
 * Format a number as currency based on locale and currency code
 * 
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param {string} language - The language code (e.g., 'en', 'fr')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'USD', language = 'en') => {
  // Bail out early when the amount is not a finite number to avoid `$NaN` results
  if (!Number.isFinite(Number(amount))) {
    return `${amount ?? ''} ${currencyCode}`.trim();
  }

  try {
    // Map language code to appropriate locale
    const localeMap = {
      'en': 'en-US',
      'fr': 'fr-FR',
      'fr-CA': 'fr-CA',
      'tr': 'tr-TR'
    };

    const locale = localeMap[language] || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${currencyCode} ${Number(amount).toFixed(2)}`;
  }
};

/**
 * Parse a formatted currency string back to a number
 * 
 * @param {string} formattedAmount - The formatted currency string
 * @returns {number} - The parsed amount as a number
 */
export const parseCurrencyValue = (formattedAmount) => {
  if (!formattedAmount) return 0;

  // Strip everything except digits, separators, and minus sign
  const cleaned = formattedAmount.replace(/[^\d.,-]/g, '');
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const lastSepIndex = Math.max(lastComma, lastDot);

  // If there is a separator, treat the last one as decimal and everything before as thousands separators
  if (lastSepIndex !== -1) {
    const integerPart = cleaned.slice(0, lastSepIndex).replace(/[.,]/g, '');
    const fractionalPart = cleaned.slice(lastSepIndex + 1);
    const normalized = `${integerPart}.${fractionalPart}`;
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  const parsed = parseFloat(cleaned.replace(/[.,]/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};